import React from 'react';
import { useParams, Link } from 'react-router-dom';
import FadeInSection from '../components/FadeInSection';

interface BlogPostData {
  slug: string;
  title: string;
  date: string;
  excerpt: string;
  content: React.ReactNode;
}

const blogPostsData: BlogPostData[] = [
  {
    slug: "how-i-learned-dvorak-in-3-weeks",
    title: "How I Learned Dvorak in 3 Weeks",
    date: "2022-08-15",
    excerpt: "...and built aoeu: a TUI Dvorak trainer for developers.",
    content: (
      <div className="space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-semibold text-text-white mb-4">
            How I Learned Dvorak in 3 Weeks
          </h1>
          <p className="text-md text-text-gray italic">
            ...and built <code className="bg-gray-800 px-2 py-1 rounded-lg border border-gray-600">aoeu</code>: a TUI Dvorak trainer for developers.
          </p>
        </div>

        <div className="space-y-6">
          <FadeInSection>
            <h2 className="text-2xl font-semibold text-text-white mb-6">
              The Case for Dvorak
            </h2>
            <div className="space-y-4 text-text-gray leading-relaxed">
              <p>
                The Dvorak Simplified Keyboard is an ergonomic alternative to the traditional QWERTY layout. Despite the similar spelling, it's unrelated to composer Antonín Dvořák.
              </p>
              <p>
                Over a winter break during college, I set a personal challenge: become proficient in a new keyboard layout within three weeks—just in time for the spring semester. While layouts like Colemak are closer to QWERTY and arguably easier to learn, I chose Dvorak. Why?
              </p>
              <p>
                Dvorak and QWERTY share only four key positions: <code className="bg-gray-800 px-2 py-1 rounded-lg border border-gray-600">~</code>, <code className="bg-gray-800 px-2 py-1 rounded-lg border border-gray-600">\\</code>, <code className="bg-gray-800 px-2 py-1 rounded-lg border border-gray-600">a</code>, and <code className="bg-gray-800 px-2 py-1 rounded-lg border border-gray-600">m</code>. While this makes the transition more drastic, it also minimizes cognitive interference between the two layouts.
              </p>
              <p>
                My primary motivation was ergonomics—not speed. Having previously dealt with minor RSI symptoms, I wanted a layout that emphasized finger travel efficiency. Based on multiple studies and personal research, Dvorak consistently ranked highly in this regard without being completely obscure.
              </p>
              <p>
                After watching Penguify's <em>"How I went from 10 to 130 WPM in 3 months"</em> (focused on Colemak), I noticed that many users reported temporary loss of QWERTY proficiency due to layout similarity. Dvorak, being dramatically different, avoids this overlap. During my initial transition, I retained my QWERTY speed despite barely using it.
              </p>
            </div>
          </FadeInSection>

          <FadeInSection>
            <h2 className="text-2xl font-semibold text-text-white mb-6">
              My Learning Process
            </h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-text-white mb-4">1. Fundamentals with TypingClub</h3>
                <p className="text-text-gray leading-relaxed">
                  I began with <a href="https://www.typingclub.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-text-white transition-colors underline">TypingClub</a> to internalize the Dvorak key positions. I was meticulous about finger placement and never looked at my hands. I repeated lessons until I could slowly touch-type without visual aids.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-white mb-4">2. Accuracy First with Monkeytype</h3>
                <p className="text-text-gray leading-relaxed">
                  Next, I practiced short quotes on <a href="https://monkeytype.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-text-white transition-colors underline">Monkeytype</a>, prioritizing over 96% accuracy over speed. At first, typing was mentally exhausting; even hitting 20 WPM felt like a milestone. But once I reached ~30 WPM, I began incorporating freeform writing.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-white mb-4">3. Writing vs Reading</h3>
                <p className="text-text-gray leading-relaxed">
                  Typing from memory (i.e., writing) was noticeably harder than typing from a screen. After two challenging weeks, I averaged 60 WPM and shifted focus to developer-oriented content.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-white mb-4">4. Typing Code with Speedtyper</h3>
                <p className="text-text-gray leading-relaxed">
                  I switched to <a href="https://www.speedtyper.dev/" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-text-white transition-colors underline">speedtyper.dev</a>, which provides programming-specific practice (like Typeracer, but with code snippets). Typing syntax-heavy strings required an entirely different muscle memory, but it accelerated my comfort with the layout.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-text-white mb-4">5. Relearning Vim</h3>
                <p className="text-text-gray leading-relaxed">
                  The final hurdle was retraining in Vim. Although remapping keys is common, I opted to re-learn the default bindings. I ran through <code className="bg-gray-800 px-2 py-1 rounded-lg border border-gray-600">vimtutor</code> dozens of times until I regained proficiency. By the time classes resumed, I was comfortably using Dvorak for both writing and development tasks.
                </p>
              </div>
            </div>
          </FadeInSection>

          <FadeInSection>
            <h2 className="text-2xl font-semibold text-text-white mb-6">
              Key Takeaways
            </h2>
            <div className="border border-gray-600 p-6 rounded-lg">
              <ul className="space-y-3 text-text-gray">
                <li className="leading-relaxed">
                  <strong className="text-text-white">Take frequent breaks.</strong> Like physical training, motor memory is built during rest—not just practice. Don't expect progress within a single session.
                </li>
                <li className="leading-relaxed">
                  <strong className="text-text-white">Prioritize accuracy.</strong> Speed comes later. Hitting <code className="bg-gray-800 px-2 py-1 rounded-lg border border-gray-600">backspace</code> isn't efficient or ergonomic.
                </li>
                <li className="leading-relaxed">
                  <strong className="text-text-white">Know when to move on.</strong> Don't grind early fundamentals past the point of diminishing returns. Advance once you've reached consistent competency.
                </li>
                <li className="leading-relaxed">
                  <strong className="text-text-white">Enjoy the process.</strong> This is a long-term investment in comfort and efficiency—frustration is normal, but burnout isn't productive.
                </li>
              </ul>
            </div>
          </FadeInSection>

          <FadeInSection>
            <h2 className="text-2xl font-semibold text-text-white mb-6">
              Aoeu: Typing for Developers
            </h2>
            <div className="text-md space-y-4 text-text-gray leading-relaxed">
              <p>
                To aid my learning, I built <a href="https://github.com/tgben/aoeu" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-text-white transition-colors underline">aoeu</a>, a terminal-based typing trainer for programmers. Unlike most typing platforms that emphasize quotes or prose, <code className="bg-gray-800 px-2 py-1 rounded-lg border border-gray-600">aoeu</code> focuses on code-relevant content—shell commands, language keywords, syntax symbols, and more.
              </p>
              <p>
                Each lesson targets a narrow scope (e.g., brackets, <code className="bg-gray-800 px-2 py-1 rounded-lg border border-gray-600">if/else</code> blocks, or Bash pipelines), allowing focused muscle memory development. The name "aoeu" comes from the Dvorak home row.
              </p>

              <div className="mt-6">
                <h3 className="text-xl font-semibold text-text-white mb-4">Technical Details</h3>
                <p className="mb-4">
                  The original version was written in Python using the <code className="bg-gray-800 px-2 py-1 rounded-lg border border-gray-600">curses</code> library, but it suffered from terminal resizing issues. I later rewrote it in Go, using <a href="https://pkg.go.dev/github.com/marcusolsson/tui-go#section-readme" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-text-white transition-colors underline">tui-go</a> for a more robust UI. The application enforces correctness by requiring users to type each word accurately before moving on.
                </p>

                <div className="text-center mt-8">
                  <a href="https://github.com/tgben/aoeu" target="_blank" rel="noopener noreferrer" className="inline-block text-md text-primary hover:text-text-white transition-colors hover:underline">
                    github/tgben/aoeu
                  </a>
                </div>
              </div>
            </div>
          </FadeInSection>
        </div>
      </div>
    )
  }
];

const DvorakBlog: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  
  if (!slug) {
    return (
      <div className="max-w-4xl mx-auto text-center">
        <h1 className="text-4xl font-semibold text-text-white mb-4">Post not found</h1>
        <Link to="/" className="text-primary hover:text-text-white transition-colors">
          ← Back to home
        </Link>
      </div>
    );
  }

  const post = blogPostsData.find(p => p.slug === slug);

  if (!post) {
    return (
      <div className="max-w-4xl mx-auto text-center">
        <h1 className="text-4xl font-semibold text-text-white mb-4">Post not found</h1>
        <Link to="/" className="text-primary hover:text-text-white transition-colors">
          ← Back to home
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <Link to="/" className="text-primary hover:text-text-white transition-colors">
          ← Back to home
        </Link>
      </div>

      <FadeInSection className="p-8" initialVisible={true}>
        <header className="mb-8">
          <div className="flex items-center space-x-4 text-sm text-text-gray mb-4">
            <span>{new Date(post.date).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</span>
          </div>
        </header>

        <div className="max-w-none">
          {post.content}
        </div>
      </FadeInSection>
      <div className="mt-8">
        <Link to="/" className="text-primary hover:text-text-white transition-colors">
          ← Back to home
        </Link>
      </div>
    </div>
  );
};

export default DvorakBlog;